import type {
  CoachingExpenditureResponse,
  CoachingHistoryResponse,
  CoachingReviewResponse,
} from '@nutians/shared/schemas';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api.js';

export const COACHING_EXPENDITURE_KEY = ['coaching', 'expenditure'] as const;
export const COACHING_HISTORY_KEY = ['coaching', 'history'] as const;

export function useCoachingExpenditure() {
  return useQuery<CoachingExpenditureResponse>({
    queryKey: COACHING_EXPENDITURE_KEY,
    queryFn: async () => {
      const { data, error } = await api.GET('/me/coaching/expenditure', {});
      if (error || !data) throw new Error('GET /me/coaching/expenditure failed');
      return data as unknown as CoachingExpenditureResponse;
    },
    staleTime: 60_000,
    retry: false,
  });
}

export function useCoachingHistory() {
  return useQuery<CoachingHistoryResponse>({
    queryKey: COACHING_HISTORY_KEY,
    queryFn: async () => {
      const { data, error } = await api.GET('/me/coaching/history', {});
      if (error || !data) throw new Error('GET /me/coaching/history failed');
      return data as unknown as CoachingHistoryResponse;
    },
    staleTime: 60_000,
    retry: false,
  });
}

export function useCoachingReview() {
  const qc = useQueryClient();
  return useMutation<
    CoachingReviewResponse,
    Error,
    { action: 'preview' | 'accept' | 'keep'; customKcal?: number }
  >({
    mutationFn: async ({ action, customKcal }) => {
      const { data, error } = await api.POST('/me/coaching/review', {
        body: { action, ...(customKcal !== undefined ? { customKcal } : {}) },
      });
      if (error || !data) throw new Error('POST /me/coaching/review failed');
      return data as unknown as CoachingReviewResponse;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: COACHING_EXPENDITURE_KEY });
      void qc.invalidateQueries({ queryKey: COACHING_HISTORY_KEY });
    },
  });
}
